import type { NavItemType } from "@/components/_untitled/application/app-navigation/config.ts";
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
    SidebarNavigation
} from "@/components/_untitled/application/app-navigation/sidebar-navigation/sidebar-navigation.tsx";
import { BadgeWithDot } from "@/components/_untitled/base/badges/badges.tsx";
import { LanguageToggle } from "@/components/global/LanguageToggle.tsx";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getAssetUrl } from "@/utils/assetUrl";
import { Button } from "@/components/_untitled/base/buttons/button";
import { NavAccountCard } from "@/components/_untitled/application/app-navigation/base-components/nav-account-card.tsx";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { useCurrentAuctionHouse } from "@/hooks/useCurrentAuctionHouse";
import { auctionHouseApi } from "@/api/auctionHouseApi";
import type { AuctionHouseOption } from "@/types/auction-house";
import { Link } from "@/components/navigation/Link";
import { build, ROUTES } from "@/config/routes";
import { getUserAccessLevel } from "@/config/access";
import { AUCTION_HOUSE_ITEMS, filterSidebarItems, getItemStatus, type SidebarItem } from "@/config/sidebar";
import { MonabidLogo } from "@/components/monabid-logo.tsx";
import type { UILanguageCode } from "@/locales";

export const Sidebar = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isAuthenticated, logout } = useAuth();
    const { isAdmin } = useAdminAccess();

    // Always get current auction house context
    const { auctionHouse, auctionHouseId, slug } = useCurrentAuctionHouse();
    const activeUrl: string = location.pathname;

    // Get user's access level for filtering navigation
    const userAccessLevel = getUserAccessLevel(user?.roles || [], auctionHouseId);

    const { data: auctionHouses } = useQuery({
        queryKey: ['auctionHouses', 'admin'],
        queryFn: auctionHouseApi.getUserAuctionHouses,
        enabled: isAdmin && isAuthenticated,
    });

    const currentLang = i18n.language as UILanguageCode;
    const fallbackLang = currentLang === 'he' ? 'en' : 'he';

    const auctionHouseOptions: AuctionHouseOption[] =
        auctionHouses?.map((house) => ({
            id: house.id,
            name: house.content[currentLang]?.name || house.content[fallbackLang]?.name || '',
            icon: getAssetUrl(house.icon),
            logo: getAssetUrl(house.logo),
        })) || [];

    const handleAuctionHouseChange = (newAuctionHouseId: string) => {
        const selectedHouse = auctionHouses?.find(h => h.id === newAuctionHouseId);
        if (selectedHouse) {
            navigate(build.adminCatalogs(selectedHouse.slug));
        }
    };

    const userData = user
        ? {
              name: `${user.firstName} ${user.lastName}`,
              email: user.email,
              avatar: user.profilePicture || '',
          }
        : undefined;

    // Always use auction house items
    const baseConfig = AUCTION_HOUSE_ITEMS;

    // Filter and transform items based on user access level
    const items: NavItemType[] = useMemo(() => {
        const filteredConfig = filterSidebarItems(baseConfig, userAccessLevel);

        return filteredConfig.map(item => {
            if ('divider' in item) {
                return { label: '', divider: true };
            }

            const sidebarItem = item as SidebarItem;
            const status = getItemStatus(sidebarItem, userAccessLevel);

            // Resolve href for auction house items
            // Guard against undefined slug to prevent navigation to /admin/undefined
            const resolvedHref = typeof sidebarItem.href === 'function'
                ? (slug ? sidebarItem.href(slug) : undefined)
                : sidebarItem.href;

            // Transform children labels
            const transformedChildren = sidebarItem.children?.map(child => ({
                ...child,
                label: t(child.label),
            }));

            // Special handling for customerService badge
            const badge = sidebarItem.id === 'navigation.customerService'
                ? <BadgeWithDot type="modern" size="sm">{t("navigation.offline")}</BadgeWithDot>
                : sidebarItem.badge;

            return {
                label: t(sidebarItem.label),
                icon: sidebarItem.icon,
                // For disabled items, don't pass href (nav-item renders as disabled)
                href: status === 'disabled' ? undefined : resolvedHref,
                items: transformedChildren,
                badge,
            };
        });
    }, [baseConfig, userAccessLevel, slug, t]);

    return (
        <SidebarNavigation
            activeUrl={ activeUrl }
            items={ items }
            logo={
                <Link href={ ROUTES.ADMIN } className="flex focus:outline-none shrink-0 gap-6 px-2">
                        <MonabidLogo className="size-32"/>
                        {auctionHouse && auctionHouse.logo && <img src={getAssetUrl(auctionHouse.logo) || undefined} alt="" className="w-16 h-8 object-contain rounded"/>}
                    </Link>
            }
            languageSwitch={
                <div key="translate" className="ps-4 pe-4">
                    <LanguageToggle />
                </div>
            }
            accountSection={
                isAuthenticated && user ? (
                    <NavAccountCard
                        user={userData}
                        auctionHouses={auctionHouseOptions}
                        selectedAuctionHouseId={auctionHouseId}
                        onAuctionHouseChange={handleAuctionHouseChange}
                        onSignOut={logout}
                    />
                ) : (
                    <Button
                        color="primary"
                        size="lg"
                        onClick={() => navigate(ROUTES.LOGIN)}
                        className="w-full"
                    >
                        { t('auth.login.signUp') }
                    </Button>
                )
            }
        />
    );
};